import moment from "moment";
import { useRef, useState } from "react";
import { Button, Icon } from "semantic-ui-react";
import { usePdfObjectUrls } from "../usePdfObjectUrls";

const fileToBase64 = (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

/**
 * Image and PDF upload for questionnaire attachment questions.
 * Hidden while the question is disabled so other payer surveys keep their current layout.
 */
export default function Attachment(props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");

  // Stable object URLs for PDFs — one per document, revoked on unmount or removal.
  const pdfObjectUrls = usePdfObjectUrls(props.surveyImage ?? {});

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      props.addAnswer(null, props.i, props.j);
      return;
    }
    setFileName(file.name);

    const MAX_SIZE = 6 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setError("This file exceeds the size limit 6MB and cannot be uploaded.");
      e.target.value = "";
      return;
    }
    setError("");

    let base64 = await fileToBase64(file);
    if (base64 && typeof base64 === "string") {
      base64 = base64.split("base64,")?.[1];
    }

    props.addAnswer(
      [
        {
          valueAttachment: {
            contentType: file.type,
            creation: moment().toISOString(),
            data: base64,
            title: file.name
          }
        }
      ],
      props.i,
      props.j
    );
    e.target.value = "";
  };

  const handleDelete = () => {
    props.addAnswer(null, props.i, props.j);
    setFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const displayImage = (valueReference: any) => {
    const referenceId = valueReference?.reference?.split("/")?.[1];
    const image = props?.surveyImage?.[referenceId];
    if (!image) return null;
    if (image.contentType === "application/pdf") {
      const url = pdfObjectUrls[referenceId];
      if (!url) return null;
      return (
        <div>
          <object key={referenceId}>
            <iframe
              title={"survey-pdf-" + referenceId}
              id={referenceId}
              width="100%"
              height="600"
              src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
            />
          </object>
        </div>
      );
    }
    return (
      <div>
        <img key={referenceId} id={referenceId} width="100%" src={image.data} alt="survey attachment" />
      </div>
    );
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  if (!props.enable) return null;

  if (props?.currentAnswer?.length > 0) {
    return (
      <div>
        {error && <p style={{ color: "red" }}>{error}</p>}
        {props.currentAnswer?.[0]?.valueReference && displayImage(props.currentAnswer?.[0]?.valueReference)}
        <div>
          <Button icon labelPosition="left" onClick={handleUploadClick}>
            <Icon name="upload" />
            Update Document Here
          </Button>
          <Button icon labelPosition="left" onClick={handleDelete}>
            <Icon name="delete" />
            Delete
          </Button>
          <input
            type="file"
            accept="image/*,application/pdf"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleChange}
          />
          {fileName && <p>Selected File: {fileName}</p>}
        </div>
      </div>
    );
  }

  return (
    <div>
      {error && <p style={{ color: "red" }}>{error}</p>}
      <Button icon labelPosition="left" onClick={handleUploadClick}>
        <Icon name="upload" />
        Upload Document Here
      </Button>
      <input
        type="file"
        accept="image/*,application/pdf"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleChange}
      />
    </div>
  );
}
